import { ActionIcon, Avatar, Box, Button, Group, SimpleGrid, Stack, Table, Text, Textarea, Tooltip } from "@mantine/core"
import { ChatTextIcon, CheckIcon, ImageIcon } from "@phosphor-icons/react"
import { Fragment, ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"
import { ReceivedQuotationApi } from "../../apis"
import { ISalesQuotation, ISalesQuotationItem } from "../../models/Quotation"
import { currencyFormat } from "../../utils"
import { dateFormat } from "../../utils/date"
import { message } from "../../utils/message"
import { renderBusinessName } from "../../utils/render-business-name"
import { translateBackendError } from "../delivery/translate-error"
import { usePermission } from "../permission"
import { ReasonTooltip } from "../ui"
import classes from "./quotation-detail.module.css"

export type QuotationAction = {
  key: string
  label: string
  allowed: boolean
  reason: string
  onClick: () => void
  primary?: boolean
  loading?: boolean
}

type Props = { data: ISalesQuotation; actions: QuotationAction[]; onCommentsSaved?: () => void }

function Card({ title, extra, children, flush }: Readonly<{ title?: ReactNode; extra?: ReactNode; children: ReactNode; flush?: boolean }>) {
  return (
    <Box className={classes.card} data-flush={flush || undefined}>
      {(title || extra) && (
        <Group justify="space-between" wrap="nowrap" className={classes.cardHead}>
          {title && <Text className={classes.cardTitle}>{title}</Text>}
          {extra}
        </Group>
      )}
      {children}
    </Box>
  )
}

function Row({ label, value, mono }: Readonly<{ label: ReactNode; value?: ReactNode; mono?: boolean }>) {
  const empty = value === undefined || value === null || value === ""
  return (
    <Group justify="space-between" wrap="nowrap" gap="md">
      <Text className={classes.kvLabel}>{label}</Text>
      <Text className={mono ? classes.kvMono : classes.kvValue} ta="right">
        {empty ? "-" : value}
      </Text>
    </Group>
  )
}

function Fact({ label, children }: Readonly<{ label: ReactNode; children: ReactNode }>) {
  return (
    <Box miw={0}>
      <Text className={classes.kvLabel}>{label}</Text>
      <Box className={classes.factValue}>{children}</Box>
    </Box>
  )
}

/**
 * Received quotation (canvas OrdB_Quotes detail panel, cards as OrdB_OrderDetail):
 * facts, the item table with a per-line comment, then actions, amounts and terms on the right.
 */
export function QuotationDetailView({ data, actions, onCommentsSaved }: Props) {
  const { t } = useTranslation(["quotation", "common"])
  const items = data.items ?? []
  const canComment = !!data.id
  // POST /pricing/received-quotation/:id/comments takes PRICING_QUOTATION_ACCEPT or _REJECT
  // (de-price-service utils/received-quotation-permission.ts). Without either the
  // control stays visible but aria-disabled, with the reason.
  const ACCEPT = usePermission("PRICING_QUOTATION_ACCEPT")
  const REJECT = usePermission("PRICING_QUOTATION_REJECT")
  const commentReason = ACCEPT.hasAccess || REJECT.hasAccess ? null : t("common:permission.required")

  // The buyer's resale view: what the same goods sell for at retail.
  let totalRetailValue = 0
  let totalDistValue = 0
  for (const it of items) {
    const retail = it.retailPrice || 0
    if (retail > 0) {
      totalRetailValue += retail * it.quantity
      totalDistValue += it.unitPrice * it.quantity
    }
  }
  const totalProfit = totalRetailValue - totalDistValue
  const overallMargin = totalRetailValue > 0 ? (totalProfit / totalRetailValue) * 100 : 0

  const initComments = () => {
    const init: Record<string, string> = {}
    for (const it of items) if (it.id && it.buyerComment) init[it.id] = it.buyerComment
    return init
  }
  const [openComments, setOpenComments] = useState<Record<string, boolean>>({})
  const [comments, setComments] = useState<Record<string, string>>(initComments)
  const [savedComments, setSavedComments] = useState<Record<string, string>>(initComments)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const toggleComment = (key: string) => setOpenComments((o) => ({ ...o, [key]: !o[key] }))

  const saveComment = async (item: ISalesQuotationItem) => {
    if (!item.id) return
    const id = item.id
    const value = comments[id] ?? ""
    setSavingKey(id)
    try {
      await ReceivedQuotationApi.saveComments(data.id, [{ itemId: id, comment: value }])
      setSavedComments((s) => ({ ...s, [id]: value }))
      message.success(t("detail.commentSaved"))
      onCommentsSaved?.()
    } catch (e) {
      message.error(translateBackendError(e))
    } finally {
      setSavingKey(null)
    }
  }

  const payment = data.paymentTermSnapshot
  const delivery = data.deliveryTermSnapshot
  const bank = data.bankAccountSnapshot
  const bankNumber = bank?.number || bank?.accountNumber
  const bankHolder = bank?.name || bank?.accountHolder
  const hasPayment = Boolean(payment?.name || payment?.description || data.termsAndConditions)
  const hasTerms = hasPayment || Boolean(delivery?.name) || Boolean(bank)
  const validity = [dateFormat(data.validFrom || data.sentAt), dateFormat(data.validUntil)].filter(Boolean).join(" ~ ")

  return (
    <Box className={classes.layout}>
      {/* ── Left: facts + items ── */}
      <Stack gap="md" miw={0}>
        <Card>
          <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="lg" verticalSpacing="md">
            <Fact label={t("detail.titleLabel")}>
              <Text className={classes.kvValue}>{data.title || "-"}</Text>
            </Fact>
            <Fact label={t("detail.supplierTitle")}>
              <Text className={classes.kvValue}>{renderBusinessName(data.supplierBusiness) || "-"}</Text>
              {data.supplierBusiness?.refCode && (
                <Text className={classes.mono} c="var(--nw-text-2)" translate="no">{data.supplierBusiness.refCode}</Text>
              )}
            </Fact>
            <Fact label={t("detail.total")}>
              <Text className={classes.mono} fw={700}>{currencyFormat(data.totalAmount || 0)}</Text>
            </Fact>
            <Fact label={t("detail.validUntil")}>
              <Text className={classes.mono}>{validity || "-"}</Text>
            </Fact>
          </SimpleGrid>
        </Card>

        <Card title={t("detail.itemsTitle")} extra={<Text className={classes.meta}>{t("detail.itemCount", { count: items.length })}</Text>} flush>
          <Box className={classes.scroll}>
            <Table className={classes.table} verticalSpacing={0} horizontalSpacing={0}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("detail.itemName")}</Table.Th>
                  <Table.Th className={classes.right}>{t("detail.itemQty")}</Table.Th>
                  <Table.Th className={classes.right}>{t("detail.itemUnitPrice")}</Table.Th>
                  <Table.Th className={classes.right}>{t("detail.itemLineTotal")}</Table.Th>
                  {canComment && <Table.Th className={classes.iconCol} aria-label={t("detail.commentAdd")} />}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((it, idx) => {
                  const key = it.id ?? String(idx)
                  const isOpen = !!openComments[key]
                  const saved = savedComments[key] ?? it.buyerComment
                  const dirty = (comments[key] ?? "") !== (savedComments[key] ?? "")
                  const retail = it.retailPrice || 0
                  return (
                    <Fragment key={key}>
                      <Table.Tr>
                        <Table.Td>
                          <Group gap="sm" wrap="nowrap">
                            <Avatar src={it.itemSnapshot?.image || null} size={40} radius="md" className={classes.thumb}>
                              <ImageIcon size={18} />
                            </Avatar>
                            <Box miw={0}>
                              <Text className={classes.lineName}>{it.customItemName || it.itemSnapshot?.nameMon || it.name || "-"}</Text>
                              {it.itemSnapshot?.skuCode && (
                                <Text className={classes.mono} c="var(--nw-text-2)" translate="no">{it.itemSnapshot.skuCode}</Text>
                              )}
                              {retail > 0 && (
                                <Text className={classes.meta}>
                                  {t("detail.retailLine", { price: currencyFormat(retail), pct: (((retail - it.unitPrice) / retail) * 100).toFixed(1) })}
                                </Text>
                              )}
                              {!isOpen && saved && <Text className={classes.comment}>{saved}</Text>}
                            </Box>
                          </Group>
                        </Table.Td>
                        <Table.Td className={`${classes.mono} ${classes.right}`}>
                          {`${it.quantity} ${it.customItemUnit || t("detail.unitPcs")}`}
                        </Table.Td>
                        <Table.Td className={`${classes.mono} ${classes.right}`}>{currencyFormat(it.unitPrice)}</Table.Td>
                        <Table.Td className={`${classes.mono} ${classes.right}`} fw={600}>{currencyFormat(it.lineTotal)}</Table.Td>
                        {canComment && (
                          <Table.Td className={classes.iconCol}>
                            {commentReason ? (
                              <ReasonTooltip reason={commentReason}>
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  size="lg"
                                  radius="md"
                                  className={classes.iconButton}
                                  data-active={isOpen || !!saved || undefined}
                                  aria-label={saved ? t("detail.commentEdit") : t("detail.commentAdd")}
                                  onClick={() => toggleComment(key)}>
                                  <ChatTextIcon size={18} />
                                </ActionIcon>
                              </ReasonTooltip>
                            ) : (
                              <Tooltip label={saved ? t("detail.commentEdit") : t("detail.commentAdd")} withArrow>
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  size="lg"
                                  radius="md"
                                  className={classes.iconButton}
                                  data-active={isOpen || !!saved || undefined}
                                  aria-label={saved ? t("detail.commentEdit") : t("detail.commentAdd")}
                                  onClick={() => toggleComment(key)}>
                                  <ChatTextIcon size={18} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </Table.Td>
                        )}
                      </Table.Tr>
                      {canComment && !commentReason && isOpen && (
                        <Table.Tr className={classes.commentRow}>
                          <Table.Td colSpan={5}>
                            <Textarea
                              value={comments[key] ?? ""}
                              onChange={(e) => {
                                const value = e.currentTarget.value
                                setComments((c) => ({ ...c, [key]: value }))
                              }}
                              placeholder={t("detail.commentPlaceholder")}
                              autosize
                              minRows={2}
                              maxRows={5}
                              data-autofocus
                            />
                            <Group justify="flex-end" mt="xs" gap="xs">
                              <Button variant="default" onClick={() => toggleComment(key)}>{t("detail.commentHide")}</Button>
                              <Button
                                color="dark"
                                loading={savingKey === key}
                                disabled={!dirty}
                                leftSection={!dirty && saved ? <CheckIcon size={16} /> : undefined}
                                onClick={() => saveComment(it)}>
                                {!dirty && saved ? t("detail.commentSavedState") : t("detail.commentSaveButton")}
                              </Button>
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Fragment>
                  )
                })}
                {items.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={5} ta="center" c="var(--nw-text-2)">{t("common:notFound")}</Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Box>
        </Card>

        {data.notes && (
          <Card title={t("detail.notesTitle")}>
            <Text className={classes.prose}>{data.notes}</Text>
          </Card>
        )}
      </Stack>

      {/* ── Right: actions, amount, terms ── */}
      <Stack gap="md" miw={0}>
        <Card title={t("common:actions.action")}>
          <Stack gap="md">
            {actions.map((a) => (
              <Stack key={a.key} gap={6} align="flex-start">
                <ReasonTooltip reason={a.allowed ? undefined : a.reason}>
                  <Button
                    variant={a.allowed && a.primary ? "filled" : "default"}
                    color={a.primary ? "brand" : undefined}
                    loading={a.loading}
                    onClick={a.onClick}>
                    {a.label}
                  </Button>
                </ReasonTooltip>
                <Text className={classes.hint}>{a.reason}</Text>
              </Stack>
            ))}
          </Stack>
        </Card>

        {data.status === "REJECTED" && data.rejectionReason && (
          <Card title={t("detail.rejectionReason")}>
            <Text className={classes.prose}>{data.rejectionReason}</Text>
          </Card>
        )}

        <Card title={t("detail.summaryTitle")}>
          <Stack gap={6}>
            <Row label={t("detail.subtotal")} value={currencyFormat(data.subtotal || 0)} mono />
            {(data.discountAmount || 0) > 0 && <Row label={t("detail.discount")} value={`-${currencyFormat(data.discountAmount || 0)}`} mono />}
            <Row label={t("detail.vat")} value={currencyFormat(data.taxAmount || 0)} mono />
            <Box className={classes.totalRow}>
              <Text fw={600}>{t("detail.total")}</Text>
              <Text className={classes.grand}>{currencyFormat(data.totalAmount || 0)}</Text>
            </Box>
            {totalRetailValue > 0 && (
              <Box className={classes.subBlock}>
                <Row label={t("detail.totalRetail")} value={currencyFormat(totalRetailValue)} mono />
                <Row label={t("detail.totalProfit")} value={`${currencyFormat(totalProfit)} · ${overallMargin.toFixed(1)}%`} mono />
              </Box>
            )}
          </Stack>
        </Card>

        {hasTerms && (
          <Card title={t("detail.termsTitle")}>
            <Stack gap="sm">
              {hasPayment && (
                <Box>
                  <Text className={classes.kvLabel}>{t("detail.paymentTerms")}</Text>
                  {payment?.name && <Text className={classes.kvValue} fw={600}>{payment.name}</Text>}
                  {payment?.description && payment.description !== payment?.name && <Text className={classes.hint}>{payment.description}</Text>}
                  {!payment?.name && !payment?.description && data.termsAndConditions && <Text className={classes.prose}>{data.termsAndConditions}</Text>}
                </Box>
              )}
              {delivery?.name && (
                <Box>
                  <Text className={classes.kvLabel}>{t("detail.deliveryTerms")}</Text>
                  <Text className={classes.kvValue} fw={600}>{delivery.name}</Text>
                  {delivery.description && delivery.description !== delivery.name && <Text className={classes.hint}>{delivery.description}</Text>}
                </Box>
              )}
              {bank && (
                <Box>
                  <Text className={classes.kvLabel}>{t("detail.bankAccount")}</Text>
                  {bank.bankName && <Text className={classes.kvValue}>{bank.bankName}</Text>}
                  {bankNumber && <Text className={classes.mono} translate="no">{bankNumber}{bank.currency ? ` · ${bank.currency}` : ""}</Text>}
                  {bankHolder && <Text className={classes.hint}>{bankHolder}</Text>}
                </Box>
              )}
            </Stack>
          </Card>
        )}
      </Stack>
    </Box>
  )
}
